/**
 * shapes.mjs — the same five facts in two shapes, and the questions only one of them can answer.
 *
 * FOUNDER, 2026-08-14: "ch3 next".
 *
 * ── THE CHAPTER SAYS WHICH SECTION MATTERS ────────────────────────────────────────────────────
 * §5 opens "This is the load-bearing section of today. Re-read it twice." — so the lab is that
 * section and only that section. It gives the entire specification: a paragraph, a table, the same
 * five facts, and five questions with the operation each one needs spelled out in brackets.
 *
 * Nothing here is invented. The paragraph is his, the five rows are his, the questions are his, and
 * every parenthetical operation ("Sum the Amount column where Day = Mon") is implemented as the
 * operation he names rather than as whatever produced the same number.
 *
 * ── WHAT THE PARAGRAPH IS ALLOWED TO DO ───────────────────────────────────────────────────────
 * Exactly one thing, because that is exactly what he grants it: "With Shape A (paragraph), a machine
 * can search for the word 'petrol' — that is about it." So searchParagraph does substring matching
 * and nothing else. It would be easy — and dishonest — to parse the numbers out of the prose and
 * quietly answer from it, and the lab would then teach the opposite of the section: it would show
 * that shape does not matter after all.
 */

/** §5 Shape A, verbatim. */
export const PARAGRAPH = 'Monday I spent 240 on groceries, then 180 on auto fare. Tuesday was quiet, just 90 on tea. Wednesday I had a big day — 4,500 on the festival shopping. Friday, 320 on petrol.';

/** §5 Shape B, verbatim — the same five facts. */
export const ROWS = Object.freeze([
  { day: 'Mon', category: 'Groceries', amount: 240 },
  { day: 'Mon', category: 'Transport', amount: 180 },
  { day: 'Tue', category: 'Tea', amount: 90 },
  { day: 'Wed', category: 'Festival', amount: 4500 },
  { day: 'Fri', category: 'Petrol', amount: 320 },
]);
export const SOURCE = 'Code101 module 03 §5 "The shape decides what you can do later"';

/**
 * His questions, each with the operation he puts in brackets. The operation is not decoration: it
 * is what makes the answer a COMPUTE rather than a fact I looked up, and the UI shows it beside the
 * result so a learner sees the machine's working rather than its conclusion.
 */
export const QUESTIONS = Object.freeze([
  { id: 'monday', ask: 'How much on Monday?', op: 'Sum the Amount column where Day = Mon.',
    run: (r) => r.filter((x) => x.day === 'Mon').reduce((t, x) => t + x.amount, 0) },
  { id: 'biggest', ask: 'What is the biggest single expense?', op: 'Sort the Amount column, take the largest.',
    run: (r) => [...r].sort((a, b) => b.amount - a.amount)[0].amount },
  /* HIS QUESTION NAMES A GROUPING HIS TABLE DOES NOT CARRY, and that is worth showing rather than
     smoothing over. The operation he gives is "Group by Category, sum each", which is what runs
     here — and it returns Groceries, Transport, Tea, Festival, Petrol, not food and transport. To
     answer "food vs transport" the table would need a column nobody captured.
     That is not a flaw in the example; it is the section's own closing argument arriving early:
     "every field is a future question someone wants to be able to ask." The note is attached to the
     question so a learner meets the point where it bites, and it is phrased as what the table can
     and cannot do — never as a correction of the module. */
  { id: 'bycat', ask: 'How much on transport vs food?', op: 'Group by Category, sum each.',
    note: 'The table groups by the categories it HAS. To ask "food vs transport" it would need a column for that, which nobody captured — which is the section\'s own point: every field is a future question someone wanted to be able to ask.',
    run: (r) => Object.entries(r.reduce((m, x) => ({ ...m, [x.category]: (m[x.category] || 0) + x.amount }), {}))
      .map(([k, v]) => `${k} ${v}`).join(' · ') },
  { id: 'average', ask: 'What is the daily average?', op: 'Sum all amounts, divide by number of distinct days.',
    run: (r) => Math.round((r.reduce((t, x) => t + x.amount, 0) / new Set(r.map((x) => x.day)).size) * 100) / 100 },
  { id: 'median', ask: 'What is the median expense?', op: 'Order the amounts, take the middle one.',
    run: (r) => [...r].map((x) => x.amount).sort((a, b) => a - b)[Math.floor(r.length / 2)] },
  { id: 'largestday', ask: 'Largest day?', op: 'Group by Day, sum each, take the largest.',
    run: (r) => { const m = r.reduce((a, x) => ({ ...a, [x.day]: (a[x.day] || 0) + x.amount }), {});
      const [d, v] = Object.entries(m).sort((a, b) => b[1] - a[1])[0]; return `${d} ${v}`; } },
  { id: 'smallestcat', ask: 'Smallest category?', op: 'Group by Category, sum each, take the smallest.',
    run: (r) => { const m = r.reduce((a, x) => ({ ...a, [x.category]: (a[x.category] || 0) + x.amount }), {});
      const [c, v] = Object.entries(m).sort((a, b) => a[1] - b[1])[0]; return `${c} ${v}`; } },
]);

/** Ask the table. A real operation over the rows, every time — never a stored answer. */
export const askTable = (id, rows = ROWS) => {
  const q = QUESTIONS.find((x) => x.id === id);
  return q ? { ok: true, value: String(q.run(rows)), op: q.op, note: q.note || null } : { ok: false };
};

/**
 * Ask the paragraph. It can find a WORD and it cannot do anything else, which is the whole point.
 * Returns his own explanation of why, so the refusal is cited rather than editorialised.
 */
export const WHY_PARAGRAPH_CANNOT = 'To answer "how much did I spend Monday?" the machine would have to read the paragraph and understand it, which is a hard problem (and exactly what AI is for, with all its uncertainty).';

export function searchParagraph(word, text = PARAGRAPH) {
  const w = String(word || '').trim();
  if (!w) return { found: false, empty: true };
  const i = text.toLowerCase().indexOf(w.toLowerCase());
  return { found: i >= 0, at: i, word: w };
}

/** What the paragraph is granted, in his words. */
export const PARAGRAPH_CAN = 'With Shape A (paragraph), a machine can search for the word "petrol" — that is about it.';
export const TABLE_CAN = 'With Shape B (table), a machine can answer in milliseconds';
export const NO_AI_NEEDED = 'None of those answers require an AI. None of them require any "intelligence." They require structured data. The structure pre-paid the cost of asking questions.';

/** §2's two bins, quoted — the frame §5 rests on. */
export const BINS = Object.freeze({
  structured: 'Structured data — facts written in clean, agreed-upon slots.',
  unstructured: 'Unstructured data — facts in their raw human form.',
  source: 'Code101 module 03 §2 "Structured vs unstructured — the two big bins"',
});

/** §5's closing argument, which is the reason a learner should care beyond the demonstration. */
export const THE_DECISION = 'When you start a project — any project — the most consequential decision is what shape will I capture this in?';
export const EVERY_FIELD = 'The forms you fill in are deliberate — every field is a future question someone wants to be able to ask.';
